import { defineStore } from "pinia";
import { useSpaceStore } from "./spaceStore";
import { useTreeStore } from "./treeStore";
import { useMainStore } from "./mainStore";
import { useRouter } from "vue-router";
import { useSocketStore } from "./socketStore";
import { inject, reactive, ref, toRaw } from "vue";
import Logger from "@/utils/logger";
import { isLocationType } from "@/utils/helper";
import { useCalculationStoreOld } from "./calculationStoreOld";

export const useCalculationStore = defineStore("calculationStore", () => {
  const spaceStore = useSpaceStore();
  const treeStore = useTreeStore();
  const mainStore = useMainStore();
  const socketStore = useSocketStore();

  const router = useRouter();
  const rustWorker: Worker = inject("rustWorker");

  let rustWorkerReady: boolean = false;

  const toUpload = reactive(new Set<string>());
  const calculating = ref(false);

  const calcOld = useCalculationStoreOld();

  let uploadData: Record<string, calculationElement> = {};

  rustWorker.onmessage = (messageEvent) => {
    if (!messageEvent.data) return;

    if (messageEvent.data.type === "ready") {
      rustWorkerReady = true;
      if (calculating.value) {
        rustWorker.postMessage({
          task: "calculateNext"
        });
      }
      Logger.log("OnMessage", messageEvent.data);
      if (Object.keys(spaceStore.calculationElements).length > 0) {
        rustWorker.postMessage({
          task: "init",
          data: { data: toRaw(spaceStore.calculationElements) }
        });
      }
      return;
    }

    if (messageEvent.data && messageEvent.data.type === "test") {
      let rust = messageEvent.data.data;
      rust.normal_elements = Object.fromEntries(rust.normal_elements);
      rust.extra_elements = Object.fromEntries(rust.extra_elements);

      let normal_queue =
        JSON.stringify(rust.normal_queue) ===
        JSON.stringify(calcOld.focusCalculation.normalQueue);
      let down_queue =
        JSON.stringify(rust.down_queue) ===
        JSON.stringify(calcOld.focusCalculation.outputQuantityQueue);

      let normal_elements = Object.entries(
        calcOld.focusCalculation.normalElements
      ).reduce((result, [key, items]) => {
        if (
          JSON.stringify(items) !== JSON.stringify(rust.normal_elements[key])
        ) {
          console.log(
            "focusElement-",
            key,
            JSON.stringify(items),
            JSON.stringify(rust.normal_elements[key])
          );
        }

        if (rust.normal_elements[key] === undefined) {
          rust.normal_elements[key] = [];
        }
        return (
          result &&
          JSON.stringify(items) === JSON.stringify(rust.normal_elements[key])
        );
      }, true);

      let extra_elements = Object.entries(
        calcOld.focusCalculation.extraElements
      ).reduce((result, [key, items]) => {
        if (
          JSON.stringify(items) !== JSON.stringify(rust.extra_elements[key])
        ) {
          console.log(
            "focusElement-",
            key,
            JSON.stringify(items),
            JSON.stringify(rust.extra_elements[key])
          );
        }
        return (
          result &&
          JSON.stringify(items) === JSON.stringify(rust.extra_elements[key])
        );
      }, true);

      // console.log(
      //   "focusElement-ready",
      //   normal_queue,
      //   down_queue,
      //   normal_elements,
      //   extra_elements
      // );
      console.log("focusElement-rust", rust);
      console.log(
        "focusElement-js",
        structuredClone(toRaw(calcOld.focusCalculation))
      );
      console.warn(
        "rust focus error",
        normal_queue,
        down_queue,
        normal_elements,
        extra_elements
      );
      if (!normal_queue || !down_queue || !normal_elements || !extra_elements) {
        console.error(
          "rust focus error",
          normal_queue,
          down_queue,
          normal_elements,
          extra_elements
        );
      }
      calcOld.clearFocusQueue();
      return;
    }

    if (messageEvent.data.type === "toUpload") {
      let x = [];
      x.toSorted;
      console.log("toUpload Rust", messageEvent.data.data.toSorted());
      console.log(
        "toUpload Old",
        toRaw(calcOld.focusCalculation.toUpload).toSorted()
      );
      for (const element of messageEvent.data.data) {
        toUpload.add(element);
      }
      return;
    }

    if (messageEvent.data.type === "result") {
      // Logger.log(
      //   "OnMessage",
      //   JSON.stringify(messageEvent.data),
      //   messageEvent.data
      // );

      if (messageEvent.data.data === false) {
        Logger.log("OnMessage", "Calculation finished");
        calculating.value = false;
        if (Object.keys(uploadData).length > 0) {
          spaceStore.updateMultipleElements(uploadData);
          uploadData = {};
        }
        return;
      }
      const changedElements: Record<string, string[]> = {};
      for (const element of messageEvent.data.data) {
        if (!spaceStore.elementExists(element.id, element.field)) continue;

        spaceStore.calculationElements[element.id][element.field] =
          element.value;

        if (toUpload.delete(element.id + "|" + element.field)) {
          // Logger.log(
          //   "OnMessage",
          //   "Deleted from toUpload",
          //   element.id,
          //   element.field
          // );
          changedElements[element.id] = changedElements[element.id] || [];
          changedElements[element.id].push(element.field);

          uploadData[element.id] = {
            ...uploadData[element.id],
            [element.field]: element.value
          };
        }
      }

      // for (const [element_id, fields] of Object.entries(changedElements)) {
      //   spaceStore.updateElement(element_id, fields);
      // }

      // Logger.log("OnMessage", "Current-Step finished", calculating.value);

      if (calculating.value) {
        // Logger.log("OnMessage", "Next-Step starting");
        rustWorker.postMessage({
          task: "calculateNext"
        });
      }
    }
  };

  function getLocationDependencies(id: string): string[] {
    if (isLocationType(id)) {
      const location_id = id.endsWith("location")
        ? id
        : spaceStore.calculationElements[id].location;
      if (!location_id) {
        return getLocationDependencies(
          spaceStore.calculationElements[id].parent
        );
      }

      // list of all elements that use current location
      return treeStore.locationDependencies[location_id] || [];
    }
  }

  function removeElementCalculation(
    element_id: string,
    fromSocket: boolean = false
  ) {
    rustWorker.postMessage({
      task: "removeElementCalculation",
      data: {
        element_id,
        location_deps: getLocationDependencies(element_id),
        fromSocket
      }
    });
  }

  function addElementCalculation(
    elementId: string,
    fromSocket: boolean = false
  ) {
    rustWorker.postMessage({
      task: "addElementCalculation",
      data: {
        element_id: elementId,
        location_deps: getLocationDependencies(elementId),
        fromSocket
      }
    });
  }

  function focusElement(id: string, field: string, key: string = "local") {
    // console.log("locationDeps", getLocationDependencies(id));
    //
    calcOld.focusElement(id, field);
    rustWorker.postMessage({
      task: "focusElement",
      data: {
        id,
        field,
        location_deps: getLocationDependencies(id),
        key
      }
    });
  }

  function preChangeValue(element_id: string, field: string, value: string) {
    return;
    switch (field) {
      default:
      // do nothing
    }
  }

  function postChangeValue(element_id: string, field: string, value: string) {
    switch (field) {
      case "name":
      case "variant_name":
      case "manufacturing_step_name":
        treeStore.changeInHierachy(element_id);
        break;
      case "location":
        const realLocation = value.endsWith("_part")
          ? treeStore.elementLocation[value]
          : value;
        const shiftModel =
          spaceStore
            .getElementList(realLocation, "shiftModel")
            .filter((element) => element.active_shiftModel === "1")
            ?.at(0)?.fullId ?? "";

        addElementCalculation(realLocation);

        const [changed, missing] = treeStore.changeRealLocations(element_id);
        spaceStore.updateMissingInputs(missing);
        addElementCalculation(realLocation);
        focusElement(element_id, "shift_model_part");
        changeValue(element_id, "shift_model_part", shiftModel);
      default:
      // do nothing
    }
  }

  function changeNormalValue(element_id: string, field: string, value: string) {
    preChangeValue(element_id, field, value);
    spaceStore.calculationElements[element_id][field] = value;
    spaceStore.updateElement(element_id, [field]);
    postChangeValue(element_id, field, value);
  }

  function changeValue(element_id: string, field: string, value: string) {
    Logger.debug("ChangeValue", element_id, field, value);

    spaceStore.removeMissingInput(element_id, field);

    if (value === spaceStore.calculationElements[element_id][field]) {
      // Value didnt change, so dont calculate
      // TODO when live, remove this. For now is relevant to control everything calculates correctly
      // calculateOnlyCurrent(element_id, field, value);
      // return;
    }

    preChangeValue(element_id, field, value);

    spaceStore.calculationElements[element_id][field] = value;

    spaceStore.updateElement(element_id, [field], true);

    rustWorker.postMessage({
      task: "change",
      data: {
        id: element_id,
        field,
        value: typeof value === "boolean" ? Number(value).toString() : value
      }
    });

    rustWorker.postMessage({
      task: "exitFocus",
      data: { key: "local" }
    });

    startCalculating();

    postChangeValue(element_id, field, value);
  }

  function changeLevel(
    element_id: string,
    level: number,
    fromSocket: boolean = false
  ) {
    if (
      !fromSocket &&
      spaceStore.calculationElements[element_id].machine_hourly_rate_machine !==
        0 &&
      element_id.endsWith("_machine") &&
      spaceStore.calculationElements[element_id].level === 1 &&
      level === 2
    ) {
      spaceStore.calculationElements[element_id].comment +=
        "\n" +
        new Date().toLocaleString("de-DE") +
        ": Level 1 -> 2: Alter Wert: " +
        spaceStore.calculationElements[element_id].machine_hourly_rate_machine +
        " €/h";
    }

    spaceStore.calculationElements[element_id].level = level;
    spaceStore.updateElement(element_id, ["level"]);

    rustWorker.postMessage({
      task: "change",
      data: {
        id: element_id,
        field: "level",
        value: level
      }
    });

    addElementCalculation(element_id, fromSocket);
    startCalculating();
    !fromSocket && socketStore.socket.emit("change-level", element_id, level);
  }

  function calculateOnlyCurrent(
    element_id: string,
    field: string,
    value: string,
    key: string = "local"
  ) {
    rustWorker.postMessage({
      task: "change",
      data: {
        id: element_id,
        field,
        value: typeof value === "boolean" ? Number(value).toString() : value
      }
    });
    rustWorker.postMessage({
      task: "calculateCurrent",
      data: {
        id: element_id,
        key
      }
    });
  }

  function startCalculating() {
    if (calculating.value) {
      // dont start a next calculation queue
      console.log("Calculation already running");
    } else {
      console.log("Starting calculation", rustWorkerReady);
      calculating.value = true;
      if (rustWorkerReady) {
        rustWorker.postMessage({
          task: "calculateNext"
        });
      }
    }
  }

  return {
    removeElementCalculation,
    addElementCalculation,
    changeNormalValue,
    changeValue,
    changeLevel,
    focusElement,
    startCalculating,
    calculateOnlyCurrent,
    calculating
  };
});
